import {
  selectConstructionPlanBrushToken,
  selectConstructionPlanEntries,
  selectUserId
} from "../../../../redux/reducers/privateStateReducer/selectors";
import {getBuildingPrice} from "../../../economy";
import {checkIfSpecialBuilding} from "../../../buildings/checkIfSpecialBuilding";
import {CONSTRUCTION_ACTION_TYPES} from "../../../../constants/construction";
import {calculateCitiesCache} from "../../calculateCitiesCache";
import {CONSTRUCTION_PLACEMENT_KINDS, resolveConstructionPlacement} from "../resolveConstructionPlacement";
import {checkIfConstructionPlanningAvailable, getConstructionPlanPlayer} from "./getConstructionPlanContext";
import {selectHexes} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {getElementForTokenType} from "../../../factions/elementals/getElementForTokenType";
import {withOwnStartingQuarter} from "../../setup/resolveOwnStartingQuarter";
import {getUnusedElementIndex} from "../../../factions/elementals/getUnusedElementIndex";

/**
 * Запланированные строения в форме отложенных строений хода (pendingBuildings):
 * в порядке планирования и с ценами, пересчитанными так, как если бы игрок
 * строил их по одному в свой ход (скидки и бесплатная первая ферма аграризма
 * зависят от уже построенного за ход). Формы записей — как у настоящих
 * отложенных строений (SET_HEX_TOKEN / SET_SPECIAL_BUILDING), чтобы их без
 * изменений понимали расчеты ячеек, цен, действий и проверка расположения.
 */
export const buildConstructionPlanPendingBuildings = (entries, player, hexes) => {
  const pendingBuildings = [];
  // Жетон ячейки к моменту каждой записи: квартал поверх запланированной
  // дороги стоит на дороге, а не на пустой ячейке — как previousToken у
  // SET_HEX_TOKEN в свой ход (на него опирается откат, см.
  // findConstructionUndoConflict)
  const tokensSoFar = {};
  entries.forEach(({hexKey, token}) => {
    const {priceCoins, isFreeAction} = getBuildingPrice(token, player, pendingBuildings);
    if (checkIfSpecialBuilding(token)) {
      pendingBuildings.push({hexKey, token, playerId: player.id, price: priceCoins});
    } else {
      pendingBuildings.push({
        type: CONSTRUCTION_ACTION_TYPES.CONSTRUCTION,
        hexKey,
        token,
        price: priceCoins,
        previousToken: hexKey in tokensSoFar ? tokensSoFar[hexKey] : hexes[hexKey],
        isFreeAction,
      });
      tokensSoFar[hexKey] = token;
    }
  });
  return pendingBuildings;
};

/**
 * Игрок «как будто он уже построил запланированное»: монеты уменьшены на
 * стоимость плана (pendingBuildings — план в форме отложенных строений,
 * buildConstructionPlanPendingBuildings). Так же, как в свой ход монеты
 * списываются при каждой постройке
 */
export const buildConstructionPlanViewPlayer = (player, pendingBuildings) => {
  const planCost = pendingBuildings.reduce((sum, building) => sum + (building.price || 0), 0);
  return {
    ...player,
    coins: (player.coins || 0) - planCost,
    // Очистка рынка политик тратит действие только в собственный ход
    isClearedPolicyMarketThisTurn: false,
  };
};

const NO_HEXES = Object.freeze({});

// Кэш на одну запись: стату читают компоненты на каждое обновление стора, а
// useSelector нужен один и тот же объект, пока входы не изменились
let cachedPlanStatsView = null;

/**
 * Стата планирующего игрока «как будто запланированное уже построено»: монеты
 * за вычетом стоимости плана и сам план в форме отложенных строений (по нему
 * считаются занятые действия). null — планирования сейчас нет (свой ход, чужая
 * фаза, зритель, админ за ботом): показывается настоящая стата.
 *
 * Одно место на все, где игрок видит свои монеты во время планирования: шапка,
 * ярлык боковой панели игроков, его строка в списке игроков и «не по карману»
 * у цен рынка политик (кнопка рынка в шапке и сам рынок). Разойдись они —
 * игрок видел бы два разных баланса разом. План живет только у клиента, так
 * что чужая стата всегда настоящая
 *
 * @param {Object} state
 * @returns {{player: Object, pendingBuildings: Object[]}|null}
 */
export const selectConstructionPlanStatsView = (state) => {
  if (!checkIfConstructionPlanningAvailable(state)) {
    return null;
  }
  const player = getConstructionPlanPlayer(state);
  if (!player) {
    return null;
  }

  const entries = selectConstructionPlanEntries(state);
  const hexes = selectHexes(state) || NO_HEXES;
  if (cachedPlanStatsView
    && cachedPlanStatsView.player === player
    && cachedPlanStatsView.entries === entries
    && cachedPlanStatsView.hexes === hexes
  ) {
    return cachedPlanStatsView.view;
  }

  const pendingBuildings = buildConstructionPlanPendingBuildings(entries, player, hexes);
  const view = {player: buildConstructionPlanViewPlayer(player, pendingBuildings), pendingBuildings};
  cachedPlanStatsView = {player, entries, hexes, view};
  return view;
};

/**
 * Поле «с планом»: запланированные жетоны поверх hexes, запланированные
 * спецстроения поверх specialBuildings. Исходные объекты не меняются. Одно
 * место на всех, кому нужно поле так, как оно ляжет после плана: вид
 * планирования и расчет дохода запланированных гильдий и храмов
 */
export const applyConstructionPlanEntriesToField = (hexes, specialBuildings, entries) => {
  const planHexes = {...hexes};
  const planSpecialBuildings = {...specialBuildings};
  entries.forEach(({hexKey, token}) => {
    if (checkIfSpecialBuilding(token)) {
      planSpecialBuildings[hexKey] = token;
    } else {
      planHexes[hexKey] = token;
    }
  });
  return {hexes: planHexes, specialBuildings: planSpecialBuildings};
};

/**
 * Жетоны элементов Элементалей «как будто игрок уже построил запланированное»:
 * вытянутые при планировании жетоны (tokens, elementalsPlanTokensByPlayerId),
 * на которые по порядку плана потрачены элементы — так же, как в свой ход
 * каждая постройка тратит первый свободный элемент своего типа. Запись, которой
 * элемента не хватило, ничего не тратит. Исходные жетоны не меняются
 */
export const buildConstructionPlanElementalsTokens = (tokens, entries) => {
  const planTokens = (tokens || []).map((token) => ({...token}));
  entries.forEach(({token}) => {
    const element = getElementForTokenType(token);
    const elementIndex = element ? getUnusedElementIndex(element, planTokens) : -1;
    if (elementIndex !== -1) {
      planTokens[elementIndex].isUsed = true;
    }
  });
  return planTokens;
};

// Кэш на одну запись: вид пересчитывается на каждой перерисовке сцены и на
// каждом клике, а его входы (поле, план) меняются заметно реже. Дороже всего
// в нем пересчет кэша городов
let cachedSharedView = null;

// Кэш городов отдельно от вида: он считается по настоящему полю и от записей
// плана не зависит. Перепроверка плана (collectUnreachablePlanEntries) строит
// вид на каждую запись с разным набором записей — без этого кэша каждый вид
// заново обходил бы все поле
let cachedCitiesCache = null;

const getCitiesCache = (hexes, specialBuildings) => {
  if (!cachedCitiesCache || cachedCitiesCache.hexes !== hexes || cachedCitiesCache.specialBuildings !== specialBuildings) {
    cachedCitiesCache = {hexes, specialBuildings, citiesCache: calculateCitiesCache(hexes, specialBuildings)};
  }
  return cachedCitiesCache.citiesCache;
};

const buildSharedView = (shared, entries, userId) => {
  if (cachedSharedView
    && cachedSharedView.shared === shared
    && cachedSharedView.entries === entries
    && cachedSharedView.userId === userId
  ) {
    return cachedSharedView.view;
  }

  const gameState = shared.gameState;
  const players = gameState.players || [];
  const playerIndex = players.findIndex((player) => player.id === userId);
  let view = null;

  if (playerIndex !== -1) {
    const player = players[playerIndex];
    const hexes = shared.hexes || {};
    const specialBuildings = gameState.specialBuildings || {};
    const pendingBuildings = buildConstructionPlanPendingBuildings(entries, player, hexes);

    const {hexes: viewHexes, specialBuildings: viewSpecialBuildings} = applyConstructionPlanEntriesToField(
      hexes, specialBuildings, entries
    );
    const viewPlayer = buildConstructionPlanViewPlayer(player, pendingBuildings);

    view = {
      ...shared,
      hexes: viewHexes,
      gameState: {
        ...gameState,
        players: players.map((candidate, index) => index === playerIndex ? viewPlayer : candidate),
        currentPlayerIndex: playerIndex,
        pendingBuildings,
        specialBuildings: viewSpecialBuildings,
        // Store-кэш городов пересчитывается только на завершении хода и не
        // видит строений, которые сейчас ставит ходящий игрок, — к началу
        // хода игрока они уже войдут в города. План в кэш не входит, как и
        // собственные отложенные строения в свой ход
        citiesCache: getCitiesCache(hexes, specialBuildings),
        invalidPendingBuildings: [],
        // Жетоны элементов ходящего игрока здесь не нужны: у планирующего
        // Элементаля свои, вытянутые при входе в планирование
        // (constructionPlanMiddleware), с потраченными на план элементами
        elementalsConstructionTokens: buildConstructionPlanElementalsTokens(
          (gameState.elementalsPlanTokensByPlayerId || {})[player.id], entries
        ),
      },
    };
  }

  cachedSharedView = {shared, entries, userId, view};
  return view;
};

/**
 * Состояние «как будто сейчас ход игрока и он уже построил запланированное» —
 * для тех же расчетов, что и в собственный ход (ячейки строительства и их
 * подсветка, цены, действия, проверка расположения), не трогая настоящее
 * состояние партии:
 *  - текущий игрок — сам игрок, его монеты уменьшены на стоимость плана;
 *  - отложенные строения — план, а hexes/specialBuildings уже содержат его,
 *    как настоящие отложенные строения в свой ход (строения ходящего сейчас
 *    игрока там тоже есть — ровно так поле ляжет к началу хода);
 *  - выбранное строение — выбранное для планирования.
 * null — пользователь не участник партии.
 */
export const buildConstructionPlanningViewState = (state, {entries, brushToken} = {}) => {
  const planEntries = entries || selectConstructionPlanEntries(state);
  const planBrushToken = brushToken !== undefined ? brushToken : selectConstructionPlanBrushToken(state);
  const sharedView = buildSharedView(state.shared, planEntries, selectUserId(state));
  if (!sharedView) {
    return null;
  }

  return {
    ...state,
    shared: sharedView,
    private: {
      ...state.private,
      selectedEntities: {
        ...state.private?.selectedEntities,
        selectedToken: planBrushToken,
      },
    },
  };
};

/**
 * Состояние для подсветки ячеек строительства на поле. При планировании —
 * ячейки самого игрока для выбранного в план строения (без выбора — никаких:
 * чужие ячейки ходящего игрока, например от исполняемого этим клиентом хода
 * бота, в режиме плана только путали бы), в остальное время — как было.
 */
export const getConstructionMarkersState = (state) => {
  if (!checkIfConstructionPlanningAvailable(state)) {
    // В расстановке зона считается со своим стартовым кварталом: в hexes его еще нет
    return withOwnStartingQuarter(state);
  }
  return buildConstructionPlanningViewState(state) || state;
};

/**
 * Решение по клику планирования: можно ли запланировать строение token на
 * ячейке — по тем же правилам, что и настоящий клик в свой ход, но против
 * состояния планирования (элементы Элементалей — вытянутые при планировании,
 * за вычетом потраченных на план).
 */
export const resolveConstructionPlanPlacement = (state, hexKey, token) => {
  const viewState = buildConstructionPlanningViewState(state, {brushToken: token});
  if (!viewState) {
    return {kind: CONSTRUCTION_PLACEMENT_KINDS.OUTSIDE, token};
  }
  return resolveConstructionPlacement(viewState, hexKey);
};
